import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { toast } from "sonner";
import { ArrowLeft, MagnifyingGlass, ArrowsClockwise, Buildings } from "@phosphor-icons/react";

const BUCKETS = ["govt", "private", "other", "nri"];
const BUCKET_META = {
  govt:    { label: "Govt",    color: "bg-emerald-50",  border: "border-emerald-300", chip: "bg-emerald-100 text-emerald-800", accent: "text-emerald-700" },
  private: { label: "Private", color: "bg-violet-50",   border: "border-violet-300",  chip: "bg-violet-100 text-violet-800",   accent: "text-violet-700" },
  other:   { label: "Other",   color: "bg-amber-50",    border: "border-amber-300",   chip: "bg-amber-100 text-amber-800",     accent: "text-amber-700" },
  nri:     { label: "NRI",     color: "bg-rose-50",     border: "border-rose-300",    chip: "bg-rose-100 text-rose-800",       accent: "text-rose-700" },
};

export default function AdminPredictorFees() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);      // "body|category" while its API call is in flight
  const [dragging, setDragging] = useState(null);  // {body, category, from}
  const [query, setQuery] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/predictor-fees");
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load matrix");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const move = async (body, category, from, to) => {
    if (from === to) return;
    // Optimistic UI update.
    setData((d) => {
      if (!d) return d;
      const next = structuredClone(d);
      const row = next.bodies.find((b) => b.counselling_body === body);
      if (!row) return d;
      row.buckets[from] = (row.buckets[from] || []).filter((c) => c !== category);
      row.buckets[to] = [...(row.buckets[to] || []), category].sort();
      return next;
    });
    const key = `${body}|${category}`;
    setSaving(key);
    try {
      const r = await api.patch("/admin/predictor-fees", {
        counselling_body: body, category, fee_bucket: to,
      });
      toast.success(`${category} → ${BUCKET_META[to].label} · ${r.data.rows_restamped} rows re-stamped`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
      await load();     // revert on error by full refetch
    } finally { setSaving(null); }
  };

  const bodies = useMemo(() => {
    if (!data?.bodies) return [];
    if (!query.trim()) return data.bodies;
    const q = query.toLowerCase();
    return data.bodies
      .map((b) => {
        const filtered = {};
        for (const k of BUCKETS) {
          filtered[k] = (b.buckets[k] || []).filter((c) => c.toLowerCase().includes(q));
        }
        const total = BUCKETS.reduce((n, k) => n + filtered[k].length, 0);
        if (total === 0 && !b.counselling_body.toLowerCase().includes(q)) return null;
        return { ...b, buckets: total === 0 ? b.buckets : filtered };
      })
      .filter(Boolean);
  }, [data, query]);

  const restampAll = async () => {
    if (!window.confirm("Re-stamp fee_bucket on ALL cutoff rows from the current matrix?")) return;
    try {
      const r = await api.post("/admin/predictor-fees/restamp");
      toast.success(`Restamp: ${r.data.rows_modified} rows across ${r.data.combos} combos`);
    } catch (e) { toast.error("Restamp failed"); }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="admin-predictor-fees-page">
      <div className="max-w-[1500px] mx-auto px-4 sm:px-6 py-6">
        {/* Header */}
        <div className="flex items-center gap-3 mb-2">
          <Link to="/admin" className="text-xs font-bold uppercase text-zinc-500 hover:text-[#6043EF] flex items-center gap-1"
            data-testid="back-to-admin">
            <ArrowLeft size={12}/> Admin
          </Link>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-[#09090B]">
            Predictor fee mapping
          </h1>
          {data?.meta && (
            <span className="text-xs font-mono font-bold text-zinc-500">
              {data.meta.total_combos} combos · {data.meta.persisted_count} customised
            </span>
          )}
        </div>
        <p className="text-sm text-zinc-500 mb-6 max-w-3xl leading-relaxed">
          Each cell is one (counselling body × NEET category) pair. Drag a category from one column to another
          to change which fee bucket the predictor will show for that combination. Changes are saved instantly
          and re-stamped onto every matching row in <code className="font-mono text-[11px] bg-zinc-100 px-1">cutoff_summary</code>.
        </p>

        {/* Toolbar */}
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <div className="relative">
            <MagnifyingGlass size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400"/>
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Filter by body or category…"
              className="h-8 pl-8 pr-3 text-xs border border-zinc-300 rounded-md focus:outline-none focus:border-[#6043EF] w-64"
              data-testid="predictor-fee-search"
            />
          </div>
          <button
            onClick={restampAll}
            data-testid="restamp-all"
            className="h-8 px-3 text-[11px] font-bold uppercase tracking-wider border border-zinc-300 hover:border-[#6043EF] hover:text-[#6043EF] rounded-md flex items-center gap-1"
          >
            <ArrowsClockwise size={12} weight="bold"/> Re-stamp all
          </button>
          <div className="ml-auto flex items-center gap-2 text-[10px] font-bold uppercase tracking-wider">
            {BUCKETS.map((b) => (
              <span key={b} className={`px-2 py-1 rounded-full ${BUCKET_META[b].chip}`}>{BUCKET_META[b].label}</span>
            ))}
          </div>
        </div>

        {loading && <div className="p-8 text-center text-zinc-400 text-sm">Loading matrix…</div>}

        {/* Matrix */}
        <div className="space-y-3">
          {bodies.map((row) => (
            <BodyRow
              key={row.counselling_body}
              row={row}
              onDragStart={setDragging}
              onDragEnd={() => setDragging(null)}
              dragging={dragging}
              onDrop={move}
              savingKey={saving}
            />
          ))}
          {!loading && bodies.length === 0 && (
            <div className="p-8 text-center text-zinc-400 text-sm">No matches for "{query}".</div>
          )}
        </div>
      </div>
    </div>
  );
}

function BodyRow({ row, onDragStart, onDragEnd, dragging, onDrop, savingKey }) {
  return (
    <div className="border-2 border-zinc-200 rounded-lg overflow-hidden" data-testid={`body-row-${row.counselling_body}`}>
      {/* Body header */}
      <div className="bg-zinc-900 text-white px-4 py-2 flex items-center gap-2">
        <Buildings size={14} weight="bold" className="text-emerald-300"/>
        <span className="font-bold text-sm">{row.counselling_body}</span>
        <span className="text-[10px] font-mono text-zinc-400 ml-auto">{row.total} categories</span>
      </div>
      {/* 4 columns */}
      <div className="grid grid-cols-1 md:grid-cols-4 divide-y md:divide-y-0 md:divide-x divide-zinc-200">
        {BUCKETS.map((bucket) => {
          const meta = BUCKET_META[bucket];
          const isDropTarget = dragging && dragging.body === row.counselling_body && dragging.from !== bucket;
          return (
            <div
              key={bucket}
              onDragOver={(e) => { if (isDropTarget) e.preventDefault(); }}
              onDrop={(e) => {
                e.preventDefault();
                if (!dragging) return;
                if (dragging.body !== row.counselling_body) return;
                onDrop(row.counselling_body, dragging.category, dragging.from, bucket);
              }}
              className={`min-h-[80px] p-2.5 ${meta.color} ${isDropTarget ? "ring-2 ring-inset ring-[#6043EF]" : ""}`}
              data-testid={`bucket-${row.counselling_body}-${bucket}`}
            >
              <div className={`text-[10px] font-bold uppercase tracking-[0.15em] mb-1.5 ${meta.accent}`}>
                {meta.label}
                <span className="ml-1.5 font-mono text-zinc-400">({(row.buckets[bucket] || []).length})</span>
              </div>
              <div className="flex flex-wrap gap-1">
                {(row.buckets[bucket] || []).map((cat) => {
                  const busy = savingKey === `${row.counselling_body}|${cat}`;
                  return (
                    <span
                      key={cat}
                      draggable
                      onDragStart={(e) => {
                        e.dataTransfer.effectAllowed = "move";
                        onDragStart({ body: row.counselling_body, category: cat, from: bucket });
                      }}
                      onDragEnd={onDragEnd}
                      data-testid={`chip-${row.counselling_body}-${cat}`}
                      className={`inline-block px-2 py-0.5 text-[11px] font-mono font-bold rounded ${meta.chip} cursor-grab active:cursor-grabbing select-none ${busy ? "opacity-40" : "hover:ring-1 hover:ring-zinc-400"}`}
                      title={`${cat} · drag to another column to change bucket`}
                    >
                      {cat}
                    </span>
                  );
                })}
                {(row.buckets[bucket] || []).length === 0 && (
                  <span className="text-[10px] text-zinc-400 italic">empty · drop here</span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
